/**
 * Feature Flags Configuration
 *
 * Centralized feature flag management for gradual integration rollout.
 * Enables instant rollback and per-integration control.
 *
 * Usage:
 * ```typescript
 * import { isFeatureEnabled, getIntegrationFeatureFlag } from '@/features/integrations/config/featureFlags.config';
 *
 * if (isFeatureEnabled('USE_NEW_INTEGRATION_SYSTEM')) {
 *   // Use new integration system
 * }
 *
 * if (isIntegrationEnabled(Integration.jira)) {
 *   // Use new Jira integration
 * }
 * ```
 */

import type { FeatureFlags } from '@/features/integrations/types/configuration.types';
import { Integration } from '@/types';

/**
 * Feature flag state
 * In production, this would be fetched from a backend service or environment variables
 */
const featureFlags: FeatureFlags = {
  /** System-wide flag to enable new integration system */
  USE_NEW_INTEGRATION_SYSTEM: false, // Set to true to enable Phase 1 integrations

  /** Per-integration flags for granular control */
  integrationFlags: {
    // Phase 1: LOW Risk (will be enabled first)
    [Integration.legit]: false,
    [Integration.apiiro]: false,
    [Integration.godaddy]: false,
    [Integration.gsuite]: false,
    [Integration.shodan]: false,
    [Integration.censys]: false,
    [Integration.cycognito]: false,
    [Integration.securitytrails]: false,
    [Integration.palo]: true,
    [Integration.orca]: false,
    [Integration.ibmcloud]: false,
    [Integration.nexpose]: false,
    [Integration.jupiterone]: false,
    [Integration.runzero]: false,
    [Integration.sentinelone]: false,
    [Integration.email]: false,
    [Integration.slack]: false,
    [Integration.teams]: false,
    [Integration.zulip]: false,
    [Integration.gchat]: false,
    [Integration.servicenow]: false,
    [Integration.webhook]: false,
    [Integration.ns1]: false,
    [Integration.digitalocean]: true, // ✅ Phase 1: API key integration extracted
    [Integration.burpsuite]: true, // ✅ Phase 1: API key integration extracted
    [Integration.socradar]: true, // ✅ Phase 1: API key integration extracted
    [Integration.recordedfuture]: true, // ✅ Phase 1: API key integration extracted
    [Integration.imperva]: true, // ✅ Phase 1: API key integration extracted
    [Integration.dnsdb]: false,
    [Integration.akamaidns]: false,

    // Phase 2: MEDIUM Risk
    [Integration.nessus]: false,
    [Integration.tenablevm]: true, // ✅ Phase 1: Extracted and ready for testing
    [Integration.qualys]: false,
    [Integration.axonius]: false,
    [Integration.crowdstrike]: false,
    [Integration.cloudflare]: false,
    [Integration.cloudflarewaf]: false,
    [Integration.freshdesk]: false,
    [Integration.freshservice]: false,
    [Integration.invicti]: false,
    [Integration.insightvm]: false,
    [Integration.avalor]: false,
    [Integration.hook]: false,
    [Integration.gitlab]: true, // ✅ Phase 1: Source code manager extracted (simple type)

    // Phase 3: HIGH Risk - Source Code Managers
    [Integration.github]: true, // ✅ Phase 1: Source code manager extracted (custom markup + OAuth)
    [Integration.bitbucket]: true, // ✅ Phase 1: Source code manager extracted (custom markup + OAuth)
    [Integration.wiz]: false,
    [Integration['microsoft-defender']]: false,
    [Integration.xpanse]: false,
    [Integration.sharphound]: false,

    // Phase 4: CRITICAL Risk (will be enabled last with extensive testing)
    [Integration.amazon]: false, // AWS
    [Integration.azure]: false,
    [Integration.gcp]: false,
    [Integration.jira]: false, // Most critical - 10/10 complexity
    [Integration.okta]: true, // ✅ Phase 1: Extracted and ready for testing
    [Integration.pingone]: false,
    [Integration.awswaf]: false,
    [Integration.fastly]: false,
    [Integration.fastlycdn]: false,
    [Integration.entraid]: false,
    [Integration.googlesso]: false,

    // Additional integrations
    [Integration.attackiq]: false,
    [Integration.cymulate]: false,
    [Integration.safebreach]: false,
    [Integration.sevco]: false,
    [Integration.akamaicdn]: false,
    [Integration['akamai-waf']]: false,
    [Integration['snyk-apprisk']]: false,
  },
};

/**
 * Check if a feature flag is enabled
 *
 * @param flag - Feature flag name
 * @returns true if feature is enabled
 */
export function isFeatureEnabled(flag: keyof FeatureFlags): boolean {
  if (flag === 'USE_NEW_INTEGRATION_SYSTEM') {
    return featureFlags.USE_NEW_INTEGRATION_SYSTEM;
  }
  return false;
}

/**
 * Check if a specific integration is enabled
 *
 * @param integration - Integration ID
 * @returns true if integration is enabled
 */
export function isIntegrationEnabled(integration: Integration): boolean {
  // If system-wide flag is disabled, no integrations are enabled
  if (!featureFlags.USE_NEW_INTEGRATION_SYSTEM) {
    return false;
  }

  // Check integration-specific flag
  return featureFlags.integrationFlags[integration] || false;
}

/**
 * Enable a feature flag (for testing/development)
 * In production, this would update backend state
 *
 * @param flag - Feature flag to enable
 */
export function enableFeatureFlag(flag: keyof FeatureFlags): void {
  if (flag === 'USE_NEW_INTEGRATION_SYSTEM') {
    featureFlags.USE_NEW_INTEGRATION_SYSTEM = true;
  }
}

/**
 * Disable a feature flag (for rollback)
 *
 * @param flag - Feature flag to disable
 */
export function disableFeatureFlag(flag: keyof FeatureFlags): void {
  if (flag === 'USE_NEW_INTEGRATION_SYSTEM') {
    featureFlags.USE_NEW_INTEGRATION_SYSTEM = false;
  }
}

/**
 * Enable a specific integration
 *
 * @param integration - Integration ID to enable
 */
export function enableIntegration(integration: Integration): void {
  featureFlags.integrationFlags[integration] = true;
}

/**
 * Disable a specific integration (for rollback)
 *
 * @param integration - Integration ID to disable
 */
export function disableIntegration(integration: Integration): void {
  featureFlags.integrationFlags[integration] = false;
}

/**
 * Get all enabled integrations
 *
 * @returns Array of enabled integration IDs
 */
export function getEnabledIntegrations(): Integration[] {
  if (!featureFlags.USE_NEW_INTEGRATION_SYSTEM) {
    return [];
  }

  return Object.entries(featureFlags.integrationFlags)
    .filter(([, enabled]) => enabled)
    .map(([id]) => id as Integration);
}

/**
 * Get feature flag state (for debugging)
 *
 * @returns Current feature flag configuration
 */
export function getFeatureFlagState(): FeatureFlags {
  return { ...featureFlags };
}

/**
 * Rollback percentage for gradual rollout
 * Format: { integrationId: percentage }
 */
const rolloutPercentages: Record<string, number> = {};

/**
 * Set rollout percentage for an integration
 *
 * @param integration - Integration ID
 * @param percentage - Rollout percentage (0-100)
 */
export function setRolloutPercentage(
  integration: Integration,
  percentage: number
): void {
  if (percentage < 0 || percentage > 100) {
    throw new Error('Rollout percentage must be between 0 and 100');
  }
  rolloutPercentages[integration] = percentage;
}

/**
 * Get rollout percentage for an integration
 *
 * @param integration - Integration ID
 * @returns Rollout percentage (0-100)
 */
export function getRolloutPercentage(integration: Integration): number {
  return rolloutPercentages[integration] || 0;
}

/**
 * Check if integration is enabled for a specific user (based on rollout percentage)
 * Uses deterministic hashing for consistent experience
 *
 * @param integration - Integration ID
 * @param userId - User ID
 * @returns true if integration is enabled for this user
 */
export function isIntegrationEnabledForUser(
  integration: Integration,
  userId: string
): boolean {
  // If integration is fully disabled, return false
  if (!isIntegrationEnabled(integration)) {
    return false;
  }

  const percentage = getRolloutPercentage(integration);

  // 0% = disabled for everyone
  if (percentage === 0) {
    return false;
  }

  // 100% = enabled for everyone
  if (percentage === 100) {
    return true;
  }

  // Deterministic hash of userId to get a number 0-99
  // Simple hash function for demonstration - in production use better hashing
  const hash = userId.split('').reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0);
  }, 0);
  const bucket = Math.abs(hash) % 100;

  return bucket < percentage;
}
